import { isValid, parse, parseISO } from "date-fns";
import type {
  DatePreset,
  PaymentLinkRow,
  SortMode,
} from "../types/paymentLinksPanel";

const DD_MM_YYYY = "dd/MM/yyyy";

export function parsePaymentLinkCreatedMs(row: PaymentLinkRow): number {
  if (typeof row.createdAtMs === "number" && Number.isFinite(row.createdAtMs)) {
    return row.createdAtMs;
  }
  if (row.createdAt) {
    const d = parseISO(row.createdAt);
    if (isValid(d)) {
      return d.getTime();
    }
  }
  const fromLabel = parse(row.createdLabel.trim(), DD_MM_YYYY, new Date(0));
  if (isValid(fromLabel)) {
    return fromLabel.getTime();
  }
  return 0;
}

function parseAmountFromFormatted(amountFormatted: string): number {
  const normalized = amountFormatted.replace(/[^0-9.-]/g, "").replace(/,/g, "");
  const n = parseFloat(normalized);
  return Number.isNaN(n) ? 0 : n;
}

export function getPaymentLinkAmountSortKey(row: PaymentLinkRow): number {
  if (
    typeof row.amountNumeric === "number" &&
    Number.isFinite(row.amountNumeric)
  ) {
    return row.amountNumeric;
  }
  return parseAmountFromFormatted(row.amountFormatted);
}

export function resolvePaymentLinkShareUrl(
  row: PaymentLinkRow,
  origin: string
): string {
  if (
    typeof row.paymentLinkUrl === "string" &&
    /^https?:\/\//i.test(row.paymentLinkUrl)
  ) {
    return row.paymentLinkUrl;
  }

  if (!origin) {
    return "";
  }

  return `${origin}/trading/business-wallet?tab=pay-link&paylink=${encodeURIComponent(row.id)}`;
}

export function getRangeBounds(
  preset: DatePreset,
  customFrom: string,
  customTo: string
): { startMs: number; endMs: number } {
  const now = Date.now();
  if (preset === "all") {
    return { startMs: 0, endMs: now };
  }
  if (preset === "custom" && customFrom && customTo) {
    const start = new Date(customFrom);
    start.setHours(0, 0, 0, 0);
    const end = new Date(customTo);
    end.setHours(23, 59, 59, 999);
    return { startMs: start.getTime(), endMs: end.getTime() };
  }
  const endMs = now;
  let startMs = now;
  switch (preset) {
    case "24h":
      startMs = now - 24 * 60 * 60 * 1000;
      break;
    case "7d":
      startMs = now - 7 * 24 * 60 * 60 * 1000;
      break;
    case "30d":
      startMs = now - 30 * 24 * 60 * 60 * 1000;
      break;
    case "3m": {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 3);
      startMs = d.getTime();
      break;
    }
    case "6m": {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 6);
      startMs = d.getTime();
      break;
    }
    case "12m": {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      startMs = d.getTime();
      break;
    }
    default:
      startMs = 0;
  }
  return { startMs, endMs };
}

export interface FilterSortPaymentLinksInput {
  rows: PaymentLinkRow[];
  query: string;
  sortMode: SortMode;
  appliedPreset: DatePreset;
  appliedCustomFrom: string;
  appliedCustomTo: string;
}

export function filterAndSortPaymentLinks({
  rows,
  query,
  sortMode,
  appliedPreset,
  appliedCustomFrom,
  appliedCustomTo,
}: FilterSortPaymentLinksInput): PaymentLinkRow[] {
  const q = query.trim().toLowerCase();
  let out = q
    ? rows.filter(
        (row) =>
          row.name.toLowerCase().includes(q) ||
          row.reference.toLowerCase().includes(q) ||
          row.amountFormatted.toLowerCase().includes(q)
      )
    : [...rows];

  let effectivePreset: DatePreset = appliedPreset;
  if (appliedPreset === "custom" && (!appliedCustomFrom || !appliedCustomTo)) {
    effectivePreset = "all";
  }

  if (effectivePreset !== "all") {
    const { startMs, endMs } = getRangeBounds(
      effectivePreset,
      appliedCustomFrom,
      appliedCustomTo
    );
    out = out.filter((row) => {
      const t = parsePaymentLinkCreatedMs(row);
      if (!t) {
        return true;
      }
      return t >= startMs && t <= endMs;
    });
  }

  if (sortMode === "paid") {
    out = out.filter((row) => row.status === "paid");
  } else if (sortMode === "unpaid") {
    out = out.filter((row) => row.status === "unpaid");
  }

  const sorted = [...out];
  switch (sortMode) {
    case "newest":
    case "paid":
    case "unpaid":
      sorted.sort(
        (a, b) => parsePaymentLinkCreatedMs(b) - parsePaymentLinkCreatedMs(a)
      );
      break;
    case "oldest":
      sorted.sort(
        (a, b) => parsePaymentLinkCreatedMs(a) - parsePaymentLinkCreatedMs(b)
      );
      break;
    case "amount-asc":
      sorted.sort(
        (a, b) =>
          getPaymentLinkAmountSortKey(a) - getPaymentLinkAmountSortKey(b)
      );
      break;
    case "amount-desc":
      sorted.sort(
        (a, b) =>
          getPaymentLinkAmountSortKey(b) - getPaymentLinkAmountSortKey(a)
      );
      break;
    default:
      break;
  }
  return sorted;
}
